import { useContext, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import {
  CUSTOMERS_PATH,
  CUSTOMER_HEADER_ACTIONS_ID,
  DEMO_PATH,
  customerPath,
  tabLabel,
  useCanManageCustomers,
  usePartnerView,
} from "../PartnerNav/partnerNav";
import { DataContext } from "../../../Context/DataContext";
import Dashboard from "../Dashboard/Dashboard";
import AssetManagement from "../Asset Management/AssetManagement";
import Beneficiaries from "../Beneficiaries/Beneficiaries";
import Trustees from "../Trustees/Trustees";
import AssetDashboard from "../Asset Dashboard/AssetDashboard";
import Tree from "../Inheritance Tree/tree";
import config from "../../../config";
import { CUSTOMER_TABS } from "./customerData";
import { createForCustomer, loadSharedCustomer } from "./sharedCustomer";
import { proposeChanges } from "../../../Utils/partnerPropose";
import { loadPartnerPrivateKey } from "../../../Utils/partnerKeys";
import styles from "./styles/CustomerDetail.module.css";
import InitialLoading from "../../Loading/InitialLoading";

const base_url = config.base_url;

/*
  One customer's profile, opened from the edit button on the list.

  The back link, the title and the four tabs all navigate, and the tab lives in
  the URL so a profile can be linked to and survives a reload.

  Each tab renders the app's own page -- the same Dashboard, Asset Management,
  Beneficiaries and Trusted Advisors components the demo side uses -- against
  the customer instead of the signed-in partner. See useCustomerContext for how,
  and why every writer is stubbed out.

  The data is real and it is decrypted here, in this browser. The server sends
  only what the customer's sharing switches allow (Routes/PartnerUser.js), and
  the customer's key arrives wrapped to the partner's public key -- so the
  server hands over a payload it cannot read, and the partner opens it with a
  private key Ostreon has never held. See sharedCustomer.js.
*/

/*
  The app's own pages, rendered against the customer instead of the signed-in
  partner. Each tab mirrors what its real route renders, Inheritance Tree
  included, so the profile looks like the site rather than an imitation of it.
*/
const TAB_VIEWS = {
  dashboard: (
    <>
      <Dashboard />
      <Tree />
    </>
  ),
  assetmanagement: <AssetManagement />,
  /*
    The Wealth Report for this customer. It has to live here rather than at
    its own route, because navigating to /user/assetmanagement/dashboard
    would leave the profile and show the PARTNER's own figures instead of the
    customer's. Reachable both from Asset Management's breadcrumb and from
    its own tab in the strip.
  */
  wealth: <AssetDashboard />,
  beneficiaries: (
    <>
      <Beneficiaries />
      <Tree />
    </>
  ),
  advisors: (
    <>
      <Trustees />
      <Tree />
    </>
  ),
};

/*
  A read-only view of somebody else's data.

  The partner's own context is kept -- CustomerUser, BillingData, the language
  and currency all stay theirs -- and only the decrypted payload is swapped for
  the customer's. That keeps every field these pages read defined, rather than
  rebuilding thirty of them from scratch and finding the gaps one crash at a
  time.

  Every writer is then stubbed out. Left alone they would still point at the
  partner's own record, so an edit made while looking at a customer would save
  to the wrong account. Until this view has a real backend, nothing it offers
  should be able to write anything.
*/
const useCustomerContext = (shared, customer_id, onCreated, customerName) => {
  const real = useContext(DataContext);
  const noop = () => {};
  return {
    ...real,
    /*
      The customer's name is carried on the payload, taken from the list rather
      than the shared record -- the list already shows it, and the profile puts
      it in the heading. Nothing withheld is disclosed by it.
    */
    UserData: shared?.data
      ? { ...shared.data, customer_full_name: customerName || "" }
      : shared?.data,
    /*
      The customer's own key. Add Asset and Add Beneficiary encrypt with
      , so pointing it at the customer's is what lets a partner
      write a record the customer can read -- and the ONLY thing they can write
      is a new one, because partnerCreate is the only writer that is not a noop.
    */
    private_key: shared?.key,
    /*
      Append one record to the customer's account, awaiting their approval. Its
      absence is what tells the Add modals they are on the customer's own side
      and should take their normal path.
    */
    /*
      Ask for a change to something the customer already has.

      The sibling of partnerCreate, and the same shape of promise: that one
      can only append a record, this one can only queue a change against one.
      Neither can touch what the customer wrote -- the customer's own save is
      the only thing that does.

      Values are encrypted here under the customer's key, the same key
      partnerCreate encrypts with, so what is queued is something the customer
      can read and nobody else can.
    */
    partnerPropose: async (kind, record_id, fields, values, files) => {
      const result = await proposeChanges({
        customer_id,
        kind,
        record_id,
        fields,
        values,
        files,
        customer_key: shared?.key,
      });
      // Refetch so the partner sees their proposal marked on the record.
      if (result?.ok && onCreated) onCreated();
      return result;
    },
    partnerCreate: async (kind, record) => {
      const result = await createForCustomer(
        customer_id,
        shared?.key,
        kind,
        record
      );
      if (result?.ok && onCreated) onCreated();
      return result;
    },
    /*
      Read by the inheritance tree, which otherwise renders the customer's own
      keep-alive settings, their phone number and the two priority delays --
      none of which has a sharing flag, so none of which may be shown.
    */
    partnerReadOnly: true,
    setUserData: noop,
    getUserData: noop,
    Update: noop,
    edit: noop,
    setCustomerUser: noop,
    getCustomerUser: noop,
    setBillingData: noop,
    getBillingData: noop,
    setCustomerBeneficiary: noop,
    getCustomerBeneficiary: noop,
  };
};

export default function CustomerDetail() {
  const partnerView = usePartnerView();
  const canManageCustomers = useCanManageCustomers();
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [text, setText] = useState(websiteCurrentLang.content.DublicatedText);
  useEffect(() => {
    setText(websiteCurrentLang.content.DublicatedText);
  }, [websiteCurrentLang]);

  const t = (key) => text?.[key] || "";

  /*
    The decrypted payload, or null once we know there is nothing to show.
    Undefined while the two requests behind it are still in flight.
  */
  const [shared, setShared] = useState(undefined);
  /*
    Bumped after a create so the profile refetches and the new record shows up
    marked for review, rather than only appearing on the customer's screen.
  */
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let live = true;
    (async () => {
      /*
        The partner's private key, unwrapped with the password secret already in
        this session. Null means they have no keypair yet -- an account that
        predates partner sharing -- which reads the same as nothing being
        shared: there is no way to open anything.
      */
      const private_key = window.sessionStorage.getItem("ostreon_key");
      const partner_private_key = await loadPartnerPrivateKey(private_key);
      if (!live) return;
      const payload = await loadSharedCustomer(id, partner_private_key);
      if (live) setShared(payload);
    })();
    return () => {
      live = false;
    };
  }, [id, reload]);

  const [customer, setCustomer] = useState(undefined);
  useEffect(() => {
    let live = true;
    fetch(base_url + "user/partner/customers", {
      method: "POST",
      mode: "cors",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-type": "application/x-www-form-urlencoded",
        credentials: "include",
      },
      credentials: "include",
      body: ``,
    })
      .then((res) => (res.status === 200 ? res.json() : { customers: [] }))
      .then((data) => {
        if (!live) return;
        const found = (data?.customers || []).find((c) => c.user_id === id);
        /*
          Being in this partner's list is the whole of the permission. The
          profile opens for any customer of theirs -- sharing decides what is
          IN it, not whether it exists, because a partner may add an asset or a
          beneficiary for a customer who has shared nothing (see
          Routes/PartnerUser.js, /customer/shared).

          It used to also require `view === "shared"`, which quietly undid
          that: the button opened, the URL changed, and this sent the partner
          straight back to the list. A customer who is not theirs is still not
          found here, and the server checks the link again regardless, so
          typing a URL gets nobody anywhere.
        */
        setCustomer(found || null);
      })
      .catch((err) => {
        console.log(err);
        if (live) setCustomer(null);
      });
    return () => {
      live = false;
    };
  }, [id]);

  /*
    Called on every render, ahead of the guards below: it wraps useContext, and
    a hook cannot sit after a return. `shared` is undefined until the payload
    lands, and the guards make sure nothing reads the context before then. It
    sits after the customer state because it reads their name.
  */
  const customerContext = useCustomerContext(
    shared,
    id,
    () => setReload((n) => n + 1),
    customer?.name
  );

  if (!partnerView || !canManageCustomers)
    return <Navigate to={DEMO_PATH} replace />;

  /*
    The customer's own row, taken from the same endpoint the list uses. Loading
    it here rather than passing it through navigation state means a profile
    opened from a link or a reload still knows whose it is.

    Null while the request is in flight; an id that matches nothing belongs back
    at the list rather than on a blank profile.
  */
  /*
    Loading.

    Rendering nothing here collapsed the page to its header, which pulled the
    footer up and then dropped it again a moment later -- the flash. A block
    that reserves roughly the height of the loaded page keeps the footer where
    it is, so the content simply appears.

    The page's own spinner rather than the full-screen Loading overlay: nothing
    is being blocked, it is a page that has not arrived, and the overlay also
    locks scrolling while it is up.
  */
  /*
    The heading, shown the moment the customer's name is known. Reused by the
    loading state below so the page does not rebuild itself around the reader
    when the payload lands.
  */
  const heading = (
    <div className={styles.title_row}>
      <button
        type="button"
        className={styles.back_button}
        onClick={() => navigate(CUSTOMERS_PATH)}
      >
        <svg className={styles.back_chevron} viewBox="0 0 8 14" aria-hidden="true">
          <path d="M7 1 1 7l6 6" />
        </svg>
        {t("dublicatedText295")}
      </button>
      <h1 className={styles.page_title}>{customer?.name}</h1>
      {/*
        Where a page inside the profile docks its own action button -- the
        Wealth Report's "Download Financial Report" is the only one today. It
        sits on this line because that is where the page's actions belong, and
        it cannot get here on its own: the guest page's header is far below,
        under the tabs and the crumbs, with no shared positioning ancestor.

        After the title, so that below the desktop breakpoint -- where the back
        button drops into the flow too -- it lands under the name rather than
        above it.
      */}
      <div id={CUSTOMER_HEADER_ACTIONS_ID} className={styles.header_actions} />
    </div>
  );

  // Nothing known yet -- not even whose profile this is.
  if (customer === undefined)
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <InitialLoading position="relative" />
        </div>
      </div>
    );
  /*
    Null means this customer genuinely shares nothing -- the list should not
    have offered a way in, so going back to it is the right answer.
  */
  if (customer === null || shared === null)
    return <Navigate to={CUSTOMERS_PATH} replace />;

  /*
    The name is known but the shared payload is not. The heading and the tabs
    are drawn already, so only the content area waits -- which is what stops
    the page rebuilding itself around the reader.
  */
  if (shared === undefined)
    return (
      <div className={styles.page}>
        {heading}
        <nav className={styles.tabs}>
          {CUSTOMER_TABS.map((entry) => (
            <div
              key={entry.key}
              className={entry.key === (tab || "dashboard") ? styles.tab_selected : styles.tab}
            >
              {tabLabel(text, entry)}
            </div>
          ))}
        </nav>
        <div className={styles.loading}>
          <InitialLoading position="relative" />
        </div>
      </div>
    );

  /*
    Shared, but unreadable. Said out loud rather than bounced: silently
    returning to the list is indistinguishable from a broken link, and leaves
    nobody -- the partner or whoever they ask -- any way to tell what went
    wrong.
  */
  if (shared.error) {
    const reason = {
      no_key:
        "Your account's sharing key cannot be opened with your current password. This happens when the password was changed before the key was set up. Please contact Ostreon support at contact@ostreon.io and we will reissue it — your customers' data is not affected.",
      no_wrap:
        "This customer has not signed in since your account was given a sharing key, so there is nothing yet that can be opened or written on their behalf. Their key is prepared in their own browser and appears the first time they sign in. If it does not, contact Ostreon support at contact@ostreon.io.",
      wrong_key:
        "This customer's data was shared with a different key than the one on your account, so it cannot be opened. Contact Ostreon support at contact@ostreon.io and we will help you re-establish it.",
      unavailable:
        "This customer's shared data could not be loaded. Please try again in a moment, and contact Ostreon support at contact@ostreon.io if it keeps happening.",
    }[shared.error];
    return (
      <div className={styles.page}>
        {heading}
        <div className={styles.notice}>
          <p className={styles.notice_text}>{reason}</p>
        </div>
      </div>
    );
  }

  /*
    Nothing shared at all -- not an asset, not a beneficiary, not an advisor.
    Read from what came back rather than from a flag, so it cannot disagree
    with what the tabs are about to draw.
  */
  const nothingShared =
    !(shared?.data?.assets || []).length &&
    !(shared?.data?.beneficiaries || []).length &&
    !(shared?.data?.trustees || []).length;

  const active = CUSTOMER_TABS.some((x) => x.key === tab)
    ? tab
    : "dashboard";


  return (
    <div className={styles.page}>
      {heading}

      <nav className={styles.tabs}>
        {CUSTOMER_TABS.map((entry) => (
          <div
            key={entry.key}
            data-toggle="link"
            onClick={() => navigate(customerPath(id, entry.key))}
            className={entry.key === active ? styles.tab_selected : styles.tab}
          >
            {tabLabel(text, entry)}
          </div>
        ))}
      </nav>

      {/*
        An empty profile, explained.

        A partner can open any customer's profile now, shared or not (see
        Routes/PartnerUser.js, /customer/shared). With nothing shared every
        tab is empty, which on its own reads as a page that failed to load --
        so it says which of the two it is, and what can still be done from
        here.
      */}
      {nothingShared && (
        <div className={styles.empty_profile}>
          <p className={styles.notice_text}>{t("dublicatedText303")}</p>
        </div>
      )}

      <DataContext.Provider value={customerContext}>
        {/*
          Wrapped so that one tab can be let out of this page's 1280 cap --
          see .tab_full_width. Carries no class on the others, where it is a
          plain block and changes nothing.
        */}
        <div
          className={
            active === "assetmanagement" ? styles.tab_full_width : undefined
          }
        >
          {TAB_VIEWS[active]}
        </div>
      </DataContext.Provider>
    </div>
  );
}
